/**
 * As três tabelas de referência: FIPE, Fraga e o estudo de mercado.
 *
 * São consultas — leitura sobre bases grandes que vêm de fora — e por isso têm a
 * mesma forma: filtrar, paginar, olhar. O que muda é o endereço, os filtros e as
 * colunas, e isso é dado.
 *
 * **Volume medido**: FIPE 6.900, Fraga 22.192, estudo de mercado 14.534. As três
 * paginam no servidor; nenhuma é trazida inteira para o navegador.
 *
 * A Fraga tem **129 colunas** por linha. Mostrar todas seria uma tabela que
 * ninguém lê, então a tela mostra um conjunto útil e deixa abrir a linha inteira
 * quando for preciso — o dado completo está lá, só não está todo na cara.
 */

export type FiltroDeLista = {
  chave: string
  rotulo: string
  /**
   * De onde vêm as opções:
   *  `porColuna` — uma chamada por campo, com o que já foi escolhido (FIPE)
   *  `deUmaVez`  — uma chamada traz as opções de todos os campos (Fraga)
   */
  origem: "porColuna" | "deUmaVez"
}

export type ColunaDaConsulta = {
  chave: string
  rotulo: string
  numero?: boolean
  /**
   * Ano é número mas não leva separador de milhar: "2.007" não é ano nenhum.
   * Continua alinhado à direita como os outros números.
   */
  ano?: boolean
  largura?: number
  /** Verdadeiro/falso vira "sim"/"não" com destaque. */
  marcador?: boolean
}

export type Consulta = {
  chave: string
  nome: string
  rota: string
  /** Onde as opções de filtro moram, quando existem. */
  rotaDeFiltros?: string
  oQueE: string
  medido: number
  /** A API pagina; este é o tamanho que a tela pede. */
  porPagina: number
  filtros: FiltroDeLista[]
  /** Escolhas por id, para o estudo de mercado. */
  filtrosPorId?: { chave: string; rotulo: string; catalogo: "assuntos" | "sistemas" }[]
  colunas: ColunaDaConsulta[]
  ressalva?: string
  /** Mostra o registro inteiro ao clicar — para quem tem muito mais campo que coluna. */
  detalheCompleto?: boolean
  /**
   * Onde baixar o recorte inteiro, quando a API tem rota para isso.
   *
   * Leva os **mesmos filtros** da consulta e não a página — é o que a diferencia
   * de exportar o que está na tela.
   */
  rotaDeExportacao?: string
}

export const CONSULTAS: Consulta[] = [
  {
    chave: "estudo-mercado",
    nome: "Estudo de mercado",
    rota: "api/estudo-mercado",
    oQueE:
      "A frota brasileira por marca, modelo, ano e motor — e se já existe conteúdo " +
      "para cada uma. Serve para decidir o que produzir: veículo com frota grande e " +
      "sem diagrama é a próxima pauta.",
    medido: 14534,
    porPagina: 100,
    filtros: [],
    filtrosPorId: [
      { chave: "assuntoId", rotulo: "Assunto", catalogo: "assuntos" },
      { chave: "sistemaId", rotulo: "Sistema", catalogo: "sistemas" },
    ],
    ressalva:
      "A coluna “Tem conteúdo” responde ao assunto e sistema escolhidos acima. " +
      "Sem escolher nenhum, ela responde pelo acervo inteiro.",
    colunas: [
      { chave: "marca", rotulo: "Marca", largura: 130 },
      { chave: "modelo", rotulo: "Modelo", largura: 160 },
      { chave: "ano", rotulo: "Ano", numero: true, ano: true, largura: 80 },
      { chave: "motor", rotulo: "Motor", largura: 110 },
      { chave: "potencia", rotulo: "Potência", largura: 90 },
      { chave: "qtdVeiculoAno", rotulo: "Frota no ano", numero: true, largura: 120 },
      { chave: "qtdVeiculoAcumulado", rotulo: "Frota acumulada", numero: true, largura: 140 },
      { chave: "possuiDiagrama", rotulo: "Tem conteúdo", marcador: true, largura: 120 },
    ],
  },
  {
    chave: "fipe",
    nome: "Tabela FIPE",
    rota: "api/tabela-fipe",
    rotaDeFiltros: "api/tabela-fipe/filtros",
    rotaDeExportacao: "api/tabela-fipe/exportar",
    oQueE: "O catálogo FIPE com as características técnicas que o sistema usa para casar veículos.",
    medido: 6900,
    porPagina: 50,
    filtros: [
      { chave: "marca", rotulo: "Marca", origem: "porColuna" },
      { chave: "veiculo", rotulo: "Veículo", origem: "porColuna" },
      { chave: "nomeCurto", rotulo: "Nome curto", origem: "porColuna" },
      { chave: "combustivel", rotulo: "Combustível", origem: "porColuna" },
      { chave: "cilindrada", rotulo: "Cilindrada", origem: "porColuna" },
      { chave: "transmissao", rotulo: "Transmissão", origem: "porColuna" },
      { chave: "tracao", rotulo: "Tração", origem: "porColuna" },
    ],
    colunas: [
      { chave: "marca", rotulo: "Marca", largura: 120 },
      { chave: "veiculo", rotulo: "Veículo" },
      { chave: "fipeCodigo", rotulo: "Código FIPE", largura: 110 },
      { chave: "anoIni", rotulo: "De", numero: true, ano: true, largura: 70 },
      { chave: "anoFim", rotulo: "Até", numero: true, ano: true, largura: 70 },
      { chave: "combustivel", rotulo: "Combustível", largura: 110 },
      { chave: "cilindrada", rotulo: "Cilindrada", largura: 95 },
      { chave: "transmissao", rotulo: "Transmissão", largura: 120 },
      { chave: "conferido", rotulo: "Conferido", marcador: true, largura: 100 },
    ],
    detalheCompleto: true,
  },
  {
    chave: "fraga",
    nome: "Tabela Fraga",
    rota: "api/tabela-fraga",
    rotaDeFiltros: "api/tabela-fraga/filtros",
    oQueE:
      "A base técnica da Fraga: motor, transmissão, freios, pneus, consumo. É de onde " +
      "sai boa parte da especificação que o conteúdo cita.",
    medido: 22192,
    porPagina: 50,
    ressalva:
      "Cada linha tem 129 campos. A tabela mostra os que identificam o veículo; " +
      "clique numa linha para ver o registro inteiro.",
    filtros: [
      { chave: "marca_Veiculo", rotulo: "Marca", origem: "deUmaVez" },
      { chave: "nome_Veiculo", rotulo: "Veículo", origem: "deUmaVez" },
      { chave: "modelo_Veiculo", rotulo: "Modelo", origem: "deUmaVez" },
      { chave: "nome_Motor", rotulo: "Motor", origem: "deUmaVez" },
      { chave: "combustivel", rotulo: "Combustível", origem: "deUmaVez" },
      { chave: "tipo_Transmissao", rotulo: "Transmissão", origem: "deUmaVez" },
      { chave: "status_Producao", rotulo: "Produção", origem: "deUmaVez" },
    ],
    colunas: [
      { chave: "marca_Veiculo", rotulo: "Marca", largura: 120 },
      { chave: "nome_Veiculo", rotulo: "Veículo", largura: 150 },
      { chave: "modelo_Veiculo", rotulo: "Modelo" },
      { chave: "nome_Motor", rotulo: "Motor", largura: 130 },
      { chave: "combustivel", rotulo: "Combustível", largura: 110 },
      { chave: "inicio_Producao", rotulo: "De", largura: 80 },
      { chave: "final_Producao", rotulo: "Até", largura: 80 },
    ],
    detalheCompleto: true,
  },
]

export function consultaDe(chave: string): Consulta {
  return CONSULTAS.find((c) => c.chave === chave) ?? CONSULTAS[0]!
}
